import moment from 'moment';

import {
  PolicyOtherOptionName,
  WireNameValue,
} from 'scenes/common/api/quote/types';
import { YesNoAnswer } from 'scenes/common/types';
import {
  MotorFunnelState,
  SpecialDetail,
  IsInsuredSincePossess,
} from 'scenes/motor/types';
import {
  getRecentInsurer,
  isSmallBusiness,
  hasAgreedTermsAndConditions,
  hasAgreedTermsAndConditionsForSmallBusiness,
  getSpecialDetails,
  // getMarketingConsent,
  sameOvernightAndPostalAddress,
  getInsuredSincePossessed,
  hasInsuredSincePossess,
  getPromoCode,
} from 'scenes/motor/selectors';
import { VEHICLE_ID } from 'scenes/common/api/quote/marshaller/ids';
import { marshalQuoteDates } from 'scenes/common/api/quote/marshaller/dates';
import {
  toUsedDifferentAddress,
  toWireAgreedTc,
  toWireEmailOptIn,
  // toWireMarketingConsent,
  toWireOnlineDiscount,
  toWirePromoCode,
} from 'scenes/common/api/quote/marshaller/policy';

import {
  WireMotorPolicyCreateRequest,
  WireMotorPolicyResponse,
  WireMotorQuotePayload,
} from '../types';
import { policyRefPartyIds } from './party';

const marshalInsuredSincePossessed = (
  isInsuredSincePossessed: IsInsuredSincePossess
): string => {
  switch (isInsuredSincePossessed) {
    case IsInsuredSincePossess.Yes:
      return 'true';
    case IsInsuredSincePossess.No:
      return 'false';
    case IsInsuredSincePossess.HaventTakenPossession:
      return 'no';
    default:
      return '';
  }
};

const toWireSdExempt = (state: MotorFunnelState): WireNameValue[] =>
  isSmallBusiness(state)
    ? [
        {
          name: PolicyOtherOptionName.SDExempt,
          value: 'true',
        },
      ]
    : [];

const toWireAgreedSdExemptTc = (state: MotorFunnelState): WireNameValue[] =>
  isSmallBusiness(state) && hasAgreedTermsAndConditionsForSmallBusiness(state)
    ? [
        {
          name: PolicyOtherOptionName.AgreedSDExemptTc,
          value: 'true',
        },
      ]
    : [];

const toWireInsuredSincePossess = (state: MotorFunnelState): WireNameValue[] =>
  hasInsuredSincePossess(state)
    ? [
        {
          name: PolicyOtherOptionName.InsuredSincePossess,
          value: marshalInsuredSincePossessed(getInsuredSincePossessed(state)),
        },
      ]
    : [];

const toWireDriverOrPolicyHolderHistory = (
  state: MotorFunnelState
): WireNameValue[] =>
  Object.values(SpecialDetail).map((sd) => ({
    name: sd,
    value: getSpecialDetails(state).includes(sd) ? 'true' : 'false',
  }));

const toWirePolicyOtherAttributes = (
  state: MotorFunnelState
): WireNameValue[] => [
  ...toWireOnlineDiscount(),
  ...toWireSdExempt(state),
  ...toWireAgreedSdExemptTc(state),
  ...toWireAgreedTc(hasAgreedTermsAndConditions(state)),
  ...toWireDriverOrPolicyHolderHistory(state),
  ...toWireEmailOptIn(),
  ...toWirePromoCode(getPromoCode(state)),
  // ...toWireMarketingConsent(getMarketingConsent(state)),
  ...toUsedDifferentAddress(!sameOvernightAndPostalAddress(state)),
  ...toWireInsuredSincePossess(state),
];

export const toWireMotorPolicy = (
  state: MotorFunnelState,
  policyStartDate: string
): WireMotorPolicyCreateRequest => ({
  dates: marshalQuoteDates(policyStartDate),
  partyRefId: policyRefPartyIds(state),
  vehicleRefId: [VEHICLE_ID],
  previousInsurer: getRecentInsurer(state),
  otherAttributes: toWirePolicyOtherAttributes(state),
});

export const toSmallBusiness = (policy: WireMotorPolicyResponse): YesNoAnswer =>
  policy.otherAttributes?.find(
    (attr) =>
      attr.name === PolicyOtherOptionName.SDExempt && attr.value === 'true'
  )
    ? YesNoAnswer.Yes
    : YesNoAnswer.No;

export const toPolicyStartDate = (policy: WireMotorPolicyResponse): string =>
  policy.dates?.effective || '';

export const toValidPolicyStartDate = (quote: WireMotorQuotePayload): string =>
  moment(quote.policy.dates?.effective).isSameOrAfter(moment())
    ? quote.policy.dates?.effective || ''
    : moment().format('YYYY-MM-DD');

export const toRecentInsurer = (policy: WireMotorPolicyResponse): string =>
  policy.previousInsurer || '';

export const toCoverLevel = (quote: WireMotorQuotePayload): string =>
  quote.policy.otherAttributes?.find(
    (attr) => attr.name === PolicyOtherOptionName.PrevCoverLevel
  )?.value || '';
